/**
 * CmdCohortMap — the safe-surface cohort intelligence panel on Discover.
 *
 * Renamed from "Cohort Map" → "Wave Composition" per the design contract
 * (/app/docs/DESIGN_CONTRACT.md) — every user-facing surface uses the
 * user's vocabulary, not our internal one. Engine, data, signals all
 * unchanged.
 *
 * Surfaces (Tier 1, default, zero internal vocabulary):
 *   • One-sentence headline (Headline → Why → Action pattern)
 *   • Two-bucket overlap panel: "Too similar — pick one" + "Complementary — pair up"
 *   • "Build wave from this view" CTA — the one-click action (Q5)
 *   • Confidence chip ("Signal: Strong" / "Signal: Limited") instead of
 *     raw explained-variance %
 *   • Plain-English empty/degraded-state copy (Q4) — never a blank panel
 *
 * Audit boundary (/app/docs/CMD_PIPELINE_HEALTH_AUDIT.md §4 + §9):
 *   • No quantified lift / revenue claims
 *   • No "confidence %" on labels we don't compute a posterior for
 *   • No "Kuramoto / NODE / autoencoder" terminology in user copy
 *
 * Power-user math (raw patch IDs, audience-overlap %, explained_var)
 * remains behind the "Show numbers" toggle for the Tier 3 audience.
 */
import React, { useMemo, useState } from 'react';
import {
  Activity, Network, GitMerge, Info, X, Wand2,
  CheckCircle2, AlertCircle, ChevronRight,
} from 'lucide-react';
import { T, P, BG, CARD, BORDER } from './discoverConstants';

const ROLE_COLOR = {
  bridge:     '#FFB84D',
  early_peak: '#4ade80',
  long_tail:  '#9C9EFF',
  general:    '#A0A0A0',
};

const ROLE_PLAIN_LABEL = {
  bridge:     'Bridge',
  early_peak: 'Seed',
  long_tail:  'Long Tail',
  general:    'Coordinated',
};

const ROLE_ONE_LINE_REASON = {
  bridge:     'Bridges into a different audience pocket',
  early_peak: 'Patient-Zero seed — fires first',
  long_tail:  'Sustains the wave after the spike',
  general:    'Coordinated with the rest of the group',
};

// Overlap thresholds — the line between "too similar" and "complementary"
// is set conservatively per audit §3.3. Empirically these bands map to:
//   ≥ 65%: the two creators reach mostly the same audience — pick one
//   5–25%: the two creators reach different audiences — useful to pair
const OVERLAP_TOO_SIMILAR_PCT = 65;
const OVERLAP_COMPLEMENTARY_MIN = 5;
const OVERLAP_COMPLEMENTARY_MAX = 25;

// Suggested-wave default sizes per brand_scale. Mirrors the existing
// creator-scale pills on ActivateWaveModal — local brands don't get
// pushed to a national-scale recommendation, enterprise brands don't
// get pushed to a single-location-scale one.
const SUGGESTED_N_BY_SCALE = {
  single_location:    5,
  multi_location:     10,
  dtc_national:       12,
  enterprise_national: 25,
};


// ── Empty-state and degraded-state copy (Q4) ──────────────────────────
// Plain English, zero internal vocabulary, always pairs with an action.
// Never a blank panel.
const EMPTY_STATES = {
  feature_disabled: {
    headline: 'Wave Composition is off for this view.',
    body:     'Cohort signals are not enabled — your filters still work as normal.',
    action:   null,
  },
  too_few_creators: {
    headline: 'Add a few more creators to see how this list groups together.',
    body:     'Wave Composition needs at least 10 creators to surface useful sub-groups. Right now this view has too few.',
    action:   null,
  },
  single_patch: {
    headline: 'This list is one tight audience — well-suited for a focused wave.',
    body:     'No cross-niche sub-groups to break out. Want more audience diversity? Widen your filters to pull in adjacent niches.',
    action:   null,
  },
  low_variance: {
    headline: 'These creators are very similar to each other.',
    body:     'Useful for a tightly-focused wave, but Wave Composition can\'t add new structure here. Broaden filters to see how different niches would mix.',
    action:   null,
  },
  fallback_method: {
    headline: 'Some creators in this view are missing rich audience data.',
    body:     'Wave Composition is running on follower-and-platform signals only. The picks below are still useful — just a lighter-weight version of the full view.',
    action:   null,
  },
};


// ── Internal helpers ──────────────────────────────────────────────────

function emptyStateKey(signals) {
  if (!signals?.enabled) return 'feature_disabled';
  const reason = signals?.uiGuard?.reason;
  if (reason === 'limited_signal_too_few_creators') return 'too_few_creators';
  if (reason === 'limited_signal_low_variance')     return 'low_variance';
  if (reason === 'limited_signal_fallback')         return 'fallback_method';
  if ((signals?.patchCount || 0) <= 1)              return 'single_patch';
  return null; // healthy state
}

function bucketPairs(pairs) {
  // Split the audience-overlap pairs into the two buckets we surface.
  // Everything in between (25-65%) is "fine but unremarkable" — we don't
  // surface those because they're not actionable.
  const tooSimilar = [];
  const complementary = [];
  for (const p of pairs || []) {
    const o = Number(p.audience_overlap_pct || 0);
    if (o >= OVERLAP_TOO_SIMILAR_PCT)                 tooSimilar.push(p);
    else if (o >= OVERLAP_COMPLEMENTARY_MIN
             && o <= OVERLAP_COMPLEMENTARY_MAX)       complementary.push(p);
  }
  return { tooSimilar, complementary };
}

function buildSuggestedWave({ creators, tags, brandScale }) {
  // Tier-1 one-click action: turn the cohort signals into a list of
  // suggested creators with one-line reasons. Engine-side decomposition
  // already happened (in `tags`); we just rank-select per patch role.
  //
  // Selection:
  //   1. Bucket creators by patch_id.
  //   2. Within each patch, sort by engagement_rate desc (then followers desc).
  //   3. Round-robin across patches until we hit the target N.
  //   4. Prefer at least one creator per role present in the cohort.
  //
  // No quantified projection is attached — only the reason ("why selected").
  const targetN = SUGGESTED_N_BY_SCALE[brandScale] || SUGGESTED_N_BY_SCALE.dtc_national;

  const byPatch = {};
  for (const c of creators || []) {
    const t = tags[c.id];
    if (!t) continue;
    const key = t.patch_id || '_';
    if (!byPatch[key]) byPatch[key] = { role: t.patch_role, creators: [], isXplat: false };
    byPatch[key].creators.push(c);
    if (t.is_xplat_amplifier) byPatch[key].isXplat = true;
  }
  Object.values(byPatch).forEach((p) => {
    p.creators.sort((a, b) =>
      (b.engagement_rate || 0) - (a.engagement_rate || 0)
      || (b.followers || 0) - (a.followers || 0)
    );
  });

  const patchKeys = Object.keys(byPatch);
  if (patchKeys.length === 0) return [];

  const picks = [];
  const pickedIds = new Set();
  let cursor = 0;
  while (picks.length < targetN) {
    let madeProgress = false;
    for (const key of patchKeys) {
      const p = byPatch[key];
      const next = p.creators[cursor];
      if (next && !pickedIds.has(next.id)) {
        const tag = tags[next.id] || {};
        picks.push({
          creator: next,
          reason: tag.is_xplat_amplifier
            ? 'Posts to multiple platforms in one campaign'
            : (ROLE_ONE_LINE_REASON[tag.patch_role] || 'Coordinated with the group'),
        });
        pickedIds.add(next.id);
        madeProgress = true;
        if (picks.length >= targetN) break;
      }
    }
    cursor += 1;
    if (!madeProgress || cursor > 50) break; // safety stop
  }
  return picks;
}


// ── UI sub-components ─────────────────────────────────────────────────

function EmptyState({ stateKey }) {
  const copy = EMPTY_STATES[stateKey] || EMPTY_STATES.single_patch;
  return (
    <div
      className="rounded-lg px-3 py-3 flex items-start gap-2"
      style={{ background: BG, border: `1px solid ${BORDER}` }}
      data-testid="wave-composition-empty"
      data-empty-reason={stateKey}
    >
      <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: T }} />
      <div>
        <p className="text-[11px] font-semibold text-white">{copy.headline}</p>
        <p className="text-[10px] text-gray-500 mt-0.5">{copy.body}</p>
      </div>
    </div>
  );
}

function ConfidenceChip({ explainedVar }) {
  // Replaces the raw "View captures N% of cohort variance" microcopy.
  // Two states only: Strong (≥ 0.45) vs Limited (0.30–0.45). Below 0.30
  // the scatter is already hidden by the UI guard — chip doesn't render.
  if (!explainedVar || explainedVar < 0.30) return null;
  const strong = explainedVar >= 0.45;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8.5px] font-bold uppercase tracking-widest"
      style={{
        background: strong ? 'rgba(74, 222, 128, 0.12)' : 'rgba(255, 184, 77, 0.12)',
        color:      strong ? '#4ade80' : '#FFB84D',
        border:     `1px solid ${strong ? 'rgba(74, 222, 128, 0.35)' : 'rgba(255, 184, 77, 0.35)'}`,
      }}
      data-testid="wave-composition-confidence"
      data-strength={strong ? 'strong' : 'limited'}
    >
      <Activity className="h-2.5 w-2.5" />
      Signal: {strong ? 'Strong' : 'Limited'}
    </span>
  );
}

function Scatter({ tags, latentCoords, onPick }) {
  const entries = useMemo(() => Object.entries(latentCoords || {}), [latentCoords]);
  const extents = useMemo(() => {
    if (entries.length === 0) return null;
    const xs = entries.map(([, c]) => c[0]);
    const ys = entries.map(([, c]) => c[1]);
    return { xmin: Math.min(...xs), xmax: Math.max(...xs),
             ymin: Math.min(...ys), ymax: Math.max(...ys) };
  }, [entries]);
  if (!extents) return null;
  const W = 360, H = 200, PAD = 14;
  const xrange = extents.xmax - extents.xmin || 1;
  const yrange = extents.ymax - extents.ymin || 1;
  const project = ([x, y]) => [
    PAD + ((x - extents.xmin) / xrange) * (W - 2 * PAD),
    PAD + ((y - extents.ymin) / yrange) * (H - 2 * PAD),
  ];
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      data-testid="cmd-cohort-scatter"
      className="w-full h-auto"
      style={{ background: BG, borderRadius: 12, border: `1px solid ${BORDER}` }}
    >
      <line x1={W / 2} y1={PAD} x2={W / 2} y2={H - PAD} stroke="#1A1A1A" strokeDasharray="2,3" />
      <line x1={PAD} y1={H / 2} x2={W - PAD} y2={H / 2} stroke="#1A1A1A" strokeDasharray="2,3" />
      {entries.map(([cid, coords]) => {
        const [cx, cy] = project(coords);
        const tag = tags[cid] || {};
        const color = ROLE_COLOR[tag.patch_role] || ROLE_COLOR.general;
        return (
          <g key={cid} onClick={() => onPick?.(cid)} style={{ cursor: 'pointer' }}>
            {tag.is_xplat_amplifier && (
              <circle cx={cx} cy={cy} r={9} fill="none" stroke={color} strokeOpacity="0.35" />
            )}
            <circle cx={cx} cy={cy} r={tag.is_bridge ? 5 : 4} fill={color}>
              <title>{`${ROLE_PLAIN_LABEL[tag.patch_role] || 'Coordinated'} group`}</title>
            </circle>
          </g>
        );
      })}
    </svg>
  );
}

function RoleLegend({ roles }) {
  if (!roles || roles.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 text-[9px] text-gray-500">
      {roles.map((r) => (
        <span key={r} className="inline-flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: ROLE_COLOR[r] || ROLE_COLOR.general }} />
          {ROLE_PLAIN_LABEL[r] || r}
        </span>
      ))}
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: 'transparent', border: `1.5px solid ${T}` }} />
        Multi-platform
      </span>
    </div>
  );
}

function OverlapBucket({ title, subtitle, pairs, creatorIndex, anchorId, onAnchor, accent, testId }) {
  if (!pairs || pairs.length === 0) {
    return (
      <div
        className="px-2.5 py-2 rounded-lg text-[10px]"
        style={{ background: BG, border: `1px solid ${BORDER}` }}
        data-testid={`${testId}-empty`}
      >
        <p className="font-semibold text-gray-400">{title}</p>
        <p className="text-gray-600 text-[9px] mt-0.5">None in this view.</p>
      </div>
    );
  }
  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ background: BG, border: `1px solid ${BORDER}` }}
      data-testid={testId}
    >
      <div className="px-2.5 py-1.5" style={{ background: `${accent}10`, borderBottom: `1px solid ${accent}25` }}>
        <p className="text-[10px] font-bold" style={{ color: accent }}>{title}</p>
        <p className="text-[8.5px] text-gray-500 mt-0.5">{subtitle}</p>
      </div>
      <div className="divide-y" style={{ borderColor: BORDER }}>
        {pairs.slice(0, 6).map((pair) => {
          const a = creatorIndex[pair.creator_a];
          const b = creatorIndex[pair.creator_b];
          if (!a || !b) return null;
          return (
            <div
              key={`${pair.creator_a}-${pair.creator_b}`}
              className="px-2.5 py-1.5 flex items-center gap-2 text-[10px]"
            >
              <button
                type="button"
                onClick={() => onAnchor?.(pair.creator_a)}
                className="text-white hover:underline truncate max-w-[88px]"
                data-testid={`anchor-${pair.creator_a}`}
              >
                {a.handle || a.name}
              </button>
              <GitMerge className="h-3 w-3 text-gray-600 flex-shrink-0" />
              <button
                type="button"
                onClick={() => onAnchor?.(pair.creator_b)}
                className="text-white hover:underline truncate max-w-[88px]"
                data-testid={`anchor-${pair.creator_b}`}
              >
                {b.handle || b.name}
              </button>
              <span className="ml-auto text-[8.5px] text-gray-500 flex-shrink-0">
                {pair.audience_overlap_pct.toFixed(0)}% shared
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SuggestedWaveStrip({ picks, onAddAll }) {
  if (!picks || picks.length === 0) return null;
  return (
    <div
      className="rounded-lg p-3"
      style={{ background: `linear-gradient(135deg, ${T}08, ${P}06)`, border: `1px solid ${T}30` }}
      data-testid="wave-composition-suggestion"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-[11px] font-black text-white flex items-center gap-1.5">
            <Wand2 className="h-3 w-3" style={{ color: T }} />
            We'd build a wave like this from this view.
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {picks.length} creator{picks.length === 1 ? '' : 's'}, balanced across the sub-groups in this list.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddAll}
          className="flex-shrink-0 px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all"
          style={{ background: T, color: '#000', boxShadow: `0 0 14px ${T}30` }}
          data-testid="build-wave-from-view"
        >
          Add all to Wave
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {picks.slice(0, 8).map((pick) => (
          <div
            key={pick.creator.id}
            className="px-2 py-1 rounded text-[9.5px] flex items-center gap-2"
            style={{ background: 'rgba(0,0,0,0.3)' }}
            data-testid={`suggested-${pick.creator.id}`}
          >
            <CheckCircle2 className="h-2.5 w-2.5 flex-shrink-0" style={{ color: '#4ade80' }} />
            <span className="text-white font-semibold truncate max-w-[90px]">
              {pick.creator.handle || pick.creator.name}
            </span>
            <span className="text-gray-500 truncate">— {pick.reason}</span>
          </div>
        ))}
        {picks.length > 8 && (
          <div className="text-[9px] text-gray-500 italic pl-2">
            + {picks.length - 8} more
          </div>
        )}
      </div>
    </div>
  );
}


// ── Main component ───────────────────────────────────────────────────

export default function CmdCohortMap({ signals, creators, brandScale, onAddAllToWave }) {
  const [anchorId, setAnchorId] = useState(null);
  const [showNumbers, setShowNumbers] = useState(false); // Tier-3 toggle

  const creatorIndex = useMemo(() => {
    const out = {};
    (creators || []).forEach((c) => {
      if (c.id) out[c.id] = c;
      if (c.handle) out[c.handle] = c;
    });
    return out;
  }, [creators]);

  const tags = signals?.tags || {};
  const rolesPresent = useMemo(() => {
    const s = new Set();
    Object.values(tags).forEach((t) => t.patch_role && s.add(t.patch_role));
    return Array.from(s);
  }, [tags]);
  const xplatCount = useMemo(
    () => Object.values(tags).filter((t) => t.is_xplat_amplifier).length,
    [tags],
  );
  const bridgeCount = useMemo(
    () => Object.values(tags).filter((t) => t.is_bridge).length,
    [tags],
  );

  // Bucket the audience-overlap pairs (Item 4).
  const buckets = useMemo(
    () => bucketPairs(signals?.similarPairs),
    [signals?.similarPairs],
  );

  // Build the suggested-wave list (Item 5).
  const suggestedPicks = useMemo(
    () => buildSuggestedWave({ creators: creators || [], tags, brandScale }),
    [creators, tags, brandScale],
  );

  // Hide the panel entirely when the feature flag is off — keep the
  // page identical to its pre-CMD form (audit §9).
  if (!signals?.enabled) return null;

  const emptyKey = emptyStateKey(signals);

  // ── HEADLINE → WHY → ACTION (design contract) ──
  // Headline is one sentence in the user's words. Why is below it.
  // Action is the "Add all to Wave" button inside SuggestedWaveStrip.
  const headlineSentence = emptyKey
    ? null
    : (() => {
        if (bridgeCount === 0 && signals.patchCount === 1) {
          return 'This list reaches one focused audience pocket.';
        }
        const pockets = signals.patchCount;
        const bridgeBit = bridgeCount > 0
          ? ` ${bridgeCount} of them ${bridgeCount === 1 ? 'connects' : 'connect'} multiple niches.`
          : '';
        return `This list reaches ${pockets} different audience pocket${pockets === 1 ? '' : 's'}.${bridgeBit}`;
      })();

  return (
    <div
      className="rounded-2xl p-4 space-y-3 mb-4"
      style={{ background: CARD, border: `1px solid ${BORDER}` }}
      data-testid="cmd-cohort-map"
      data-surface-name="wave-composition"
    >
      {/* Header — uses "Wave Composition" not "Cohort Map" (Item 1) */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-[11px] font-black uppercase tracking-[0.18em] flex items-center gap-1.5" style={{ color: T }}>
            <Network className="h-3 w-3" />
            Wave Composition
            <span className="ml-1 px-1.5 py-0.5 rounded text-[7px] uppercase tracking-widest font-bold" style={{ background: `${T}15`, color: T }}>
              Beta
            </span>
          </h3>
          {headlineSentence && (
            <p className="text-[12px] font-semibold text-white mt-1.5" data-testid="wave-composition-headline">
              {headlineSentence}
            </p>
          )}
          {!emptyKey && (
            <p className="text-[10px] text-gray-500 mt-0.5">
              {xplatCount > 0
                ? `${xplatCount} creator${xplatCount === 1 ? '' : 's'} post${xplatCount === 1 ? 's' : ''} to 2+ platforms in one campaign.`
                : 'Pick a balanced mix below — we\'ve already grouped them for you.'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <ConfidenceChip explainedVar={signals.explainedVar} />
          {anchorId && (
            <button
              type="button"
              onClick={() => setAnchorId(null)}
              className="text-[9px] text-gray-500 hover:text-white flex items-center gap-1"
              data-testid="clear-anchor"
            >
              <X className="h-3 w-3" />
              Clear pick
            </button>
          )}
        </div>
      </div>

      {/* Degraded / empty states (Item: empty-state copy) */}
      {emptyKey ? (
        <EmptyState stateKey={emptyKey} />
      ) : (
        <>
          {/* Item 5 — Suggested Wave CTA at the top of the panel. */}
          <SuggestedWaveStrip picks={suggestedPicks} onAddAll={() => onAddAllToWave?.(suggestedPicks)} />

          {/* Item 4 — Two-bucket overlap panel: similar to skip, complementary to pair. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <OverlapBucket
              title="Too similar — pick one"
              subtitle="These pairs reach the same audience. Doubling up wastes wave spend."
              pairs={buckets.tooSimilar}
              creatorIndex={creatorIndex}
              anchorId={anchorId}
              onAnchor={setAnchorId}
              accent="#FF7A7A"
              testId="overlap-too-similar"
            />
            <OverlapBucket
              title="Complementary — pair these up"
              subtitle="These pairs reach different audiences. Combining them widens reach."
              pairs={buckets.complementary}
              creatorIndex={creatorIndex}
              anchorId={anchorId}
              onAnchor={setAnchorId}
              accent="#4ade80"
              testId="overlap-complementary"
            />
          </div>

          {/* Tier-3 "Show numbers" toggle — exposes scatter + role legend.
              Default OFF so Tier-1 users never see the math. */}
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => setShowNumbers((v) => !v)}
              className="text-[9px] text-gray-500 hover:text-white transition-colors"
              data-testid="toggle-advanced-view"
            >
              {showNumbers ? 'Hide numbers' : 'Show numbers (advanced)'}
            </button>
          </div>
          {showNumbers && !signals.uiGuard?.hide_scatter && (
            <div className="space-y-2 pt-1" data-testid="advanced-view">
              <Scatter tags={tags} latentCoords={signals.latentCoords} onPick={setAnchorId} />
              <RoleLegend roles={rolesPresent} />
              <p className="text-[8px] text-gray-600 uppercase tracking-widest">
                Variance captured: {(signals.explainedVar * 100).toFixed(0)}%
              </p>
            </div>
          )}
          {showNumbers && signals.uiGuard?.hide_scatter && (
            <p className="text-[9px] text-gray-500 italic pt-1" data-testid="advanced-view-guard">
              Numbers hidden: {signals.uiGuard.reason || 'limited signal in this view'}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
